import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AuthFooterControls } from '@/components/auth/AuthFooterControls'
import { AuthPageShell } from '@/components/auth/AuthPageShell'
import { type LoginCredentials, LoginFormView } from '@/components/auth/LoginFormView'
import { Button } from '@/components/ui/button'
import { AUTH_PROVIDER } from '@/config'
import { useAuth } from '@/contexts/AuthContext'

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)

  const { login, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/cases/home'

  // Already signed in: skip the form and go straight to the app.
  if (!isAuthLoading && isAuthenticated) {
    return <Navigate to={from} replace />
  }

  const handleSubmit = async ({ username, password }: LoginCredentials) => {
    setIsLoading(true)

    try {
      await login(username, password)
      navigate(from, { replace: true })
    } catch (error) {
      // Error toast already shown by AuthContext
      console.error('Login failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // OIDC mode: a single button kicks off the PKCE redirect to Keycloak. The
  // browser leaves this page, so there is no post-call navigation here — the
  // already-authenticated guard above handles the redirect back into the app.
  const handleOidcLogin = async () => {
    setIsLoading(true)
    try {
      await login('', '')
    } catch (error) {
      console.error('Login failed:', error)
      setIsLoading(false)
    }
  }

  return (
    <AuthPageShell>
      {AUTH_PROVIDER === 'oidc' ? (
        <Button className="w-full max-w-sm" disabled={isLoading} onClick={handleOidcLogin}>
          {isLoading ? 'Redirecting…' : 'Sign in with Keycloak'}
        </Button>
      ) : (
        <LoginFormView loading={isLoading} onSubmit={handleSubmit} />
      )}

      <AuthFooterControls />
    </AuthPageShell>
  )
}
